import React, { FC, ReactNode } from 'react'
import classNames from 'classnames'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'

const useInlineDisplayWrapperStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      alignItems: 'center',
      flexDirection: 'row',
      minHeight: 30,
      marginBottom: 0,
    },
  },
  /**
   * Shows yellow background. This is triggered
   * if the shipment has active Todos.
   */
  hasHighlight: {
    backgroundColor: brandColors.warningLight,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
  },
  labelWrapper: {
    flex: '0 0 100%',
    paddingBottom: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      fontSize: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      flex: '0 0 90px',
      paddingBottom: 0,
    },
  },
  value: {
    flex: '0 0 100%',
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      flex: '1 1 auto',
    },
  },
}))

interface IInlineDisplayWrapperProps {
  label: string
  value: ReactNode
  /**
   * Shows a yellow background highlight
   * @default false
   */
  hasHighlight?: boolean
}

export const InlineDisplayWrapper: FC<IInlineDisplayWrapperProps> = ({
  hasHighlight = false,
  label,
  value,
}) => {
  const { getFlag } = useFeatureFlagContext()
  const { enabled } = getFlag('fpCarrierPortal')
  const classes = useInlineDisplayWrapperStyles()

  /**
   * This enables some backward compatibility until
   * `fpCarrierPortal` is enabled
   */
  const ParentComponent = enabled ? Box : 'span'
  const parentClassName = enabled ? classes.root : ''
  const labelClassName = enabled ? classes.labelWrapper : 'b text-small mb2 text-secondary'
  const ValueComponent = enabled ? Box : 'div'

  const valueClassName = classNames(enabled ? classes.value : '', {
    [classes.hasHighlight]: hasHighlight,
  })

  return (
    <ParentComponent className={parentClassName}>
      <Box className={labelClassName}>
        <Typography variant='subtitle2' color='textSecondary'>
          {label}
        </Typography>
      </Box>
      <ValueComponent className={valueClassName}>{value}</ValueComponent>
    </ParentComponent>
  )
}
